/**
 * Browser OpenTelemetry bootstrap.
 *
 * Initialises a WebTracerProvider that exports OTLP/HTTP traces to the same
 * Honeycomb endpoint the backend Lambdas use, so frontend → backend traces
 * join up via traceparent propagation. Auto-instruments fetch + XHR +
 * document load; custom business spans live in their respective components
 * and call `tracer` / `withSpan` from this module.
 *
 * The Honeycomb ingest key is intentionally bundled with the client — it
 * is a write-only key, that's the standard pattern, and the user has
 * accepted the public-exposure trade-off. If abuse becomes a problem,
 * mint a separate browser-scoped key with rate limits or proxy via the
 * backend.
 */
import { trace, context, type Span, type Attributes } from '@opentelemetry/api';
import {
  WebTracerProvider,
  BatchSpanProcessor,
  type SpanProcessor,
  type ReadableSpan,
} from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
} from '@opentelemetry/semantic-conventions/incubating';
import { API_BASE_URL } from '../config/api';

// Endpoint is the same across environments. The ingest key is per-env and
// injected at build time from the deploy-frontend workflow, which reads it
// from the matching environment's terraform `honeycomb_api_key` output.
const HONEYCOMB_OTLP_URL = 'https://api.honeycomb.io/v1/traces';
const HONEYCOMB_API_KEY = import.meta.env.VITE_HONEYCOMB_API_KEY as
  | string
  | undefined;

if (!HONEYCOMB_API_KEY) {
  // Fail loudly at boot rather than silently produce traces that 401 at the
  // ingest endpoint. Better to surface "telemetry off" in Sentry than to
  // gaslight everyone about what's in Honeycomb.
  throw new Error(
    'VITE_HONEYCOMB_API_KEY is not set — frontend telemetry cannot start.'
  );
}

const SERVICE_NAME = 'daddytime-frontend';
const SERVICE_VERSION = '0.0.0';
const DEPLOYMENT_ENVIRONMENT = import.meta.env.MODE;

/**
 * The user the current browser session is logged in as, or `null` for
 * anonymous. Updated from AuthContext via setTelemetryUser. Read by the
 * UserAttributeSpanProcessor to tag every span with auth state.
 */
let currentUser: { email: string } | null = null;

export function setTelemetryUser(user: { email: string } | null): void {
  currentUser = user;
}

/**
 * SpanProcessor that decorates every starting span with the current
 * authenticated user (if any). Avoids each instrumentation point having to
 * remember to call `span.setAttribute('user.authenticated', ...)`.
 */
class UserAttributeSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    span.setAttribute('user.authenticated', currentUser !== null);
    if (currentUser) {
      span.setAttribute('user.email', currentUser.email);
    }
  }
  onEnd(_span: ReadableSpan): void {}
  shutdown(): Promise<void> {
    return Promise.resolve();
  }
  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}

const provider = new WebTracerProvider({
  resource: resourceFromAttributes({
    [ATTR_SERVICE_NAME]: SERVICE_NAME,
    [ATTR_SERVICE_VERSION]: SERVICE_VERSION,
    [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: DEPLOYMENT_ENVIRONMENT,
  }),
  spanProcessors: [
    new UserAttributeSpanProcessor(),
    new BatchSpanProcessor(
      new OTLPTraceExporter({
        url: HONEYCOMB_OTLP_URL,
        headers: {
          'x-honeycomb-team': HONEYCOMB_API_KEY,
        },
      })
    ),
  ],
});

provider.register({
  contextManager: new ZoneContextManager(),
});

// Propagate traceparent on outgoing requests to the API so backend spans
// join the same trace. The auto-fetch instrumentation handles the actual
// header injection — we just tell it which URLs to propagate to.
const apiOrigin = new URL(API_BASE_URL).origin;

/**
 * Rename auto-instrumented HTTP spans from `HTTP POST` / `POST` to
 * `POST /audio/get-url` style so the span name on its own is meaningful.
 * Falls back silently if we can't extract a URL — never throw from a hook.
 */
function renameHttpSpan(method: string, urlInput: unknown) {
  return (span: Span) => {
    try {
      const url =
        typeof urlInput === 'string'
          ? new URL(urlInput, window.location.origin)
          : urlInput instanceof URL
            ? urlInput
            : new URL((urlInput as { url: string }).url, window.location.origin);
      span.updateName(`${method} ${url.pathname}`);
    } catch {
      // leave the default name in place
    }
  };
}

registerInstrumentations({
  instrumentations: [
    // Suppress the per-PerformanceResourceTiming-event spans this normally
    // produces (fetchStart, connectEnd, domainLookupStart, …). We keep the
    // single documentLoad parent for first-paint timings, drop the rest.
    new DocumentLoadInstrumentation({ ignoreNetworkEvents: true }),
    new FetchInstrumentation({
      propagateTraceHeaderCorsUrls: [apiOrigin],
      clearTimingResources: true,
      applyCustomAttributesOnSpan: (span, request) => {
        const method =
          (request as { method?: string }).method?.toUpperCase() ?? 'HTTP';
        renameHttpSpan(method, request)(span);
      },
    }),
    new XMLHttpRequestInstrumentation({
      propagateTraceHeaderCorsUrls: [apiOrigin],
      applyCustomAttributesOnSpan: (span, xhr) => {
        const method = ((xhr as { _method?: string })._method ?? 'HTTP')
          .toUpperCase();
        renameHttpSpan(method, (xhr as { responseURL?: string }).responseURL)(
          span
        );
      },
    }),
  ],
});

/**
 * Application tracer. Use `tracer.startActiveSpan` for spans that should
 * become the parent of any subsequent spans created on the same async path
 * (e.g. wrapping a fetch will make the auto fetch span its child).
 */
export const tracer = trace.getTracer('daddytime-frontend');

/**
 * Run `fn` inside a span named `name` with the given attributes. The span
 * ends when `fn` resolves or rejects. Errors are recorded on the span.
 */
export async function withSpan<T>(
  name: string,
  attributes: Attributes,
  fn: (span: Span) => Promise<T> | T
): Promise<T> {
  return tracer.startActiveSpan(name, { attributes }, async (span) => {
    try {
      const result = await fn(span);
      span.end();
      return result;
    } catch (err) {
      span.recordException(err as Error);
      span.end();
      throw err;
    }
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// User-journey spans
//
// These are top-level "did the user manage to do the thing" spans that cross
// component boundaries (and in login's case, a full page navigation).
// They're stored in module-level state because React components mount and
// unmount along the journey — a useRef inside one component can't carry the
// span across the journey's full length.
// ─────────────────────────────────────────────────────────────────────────────

const audioReadyJourneys = new Map<string, Span>();

/**
 * Start a span covering "user clicked an audio card → audio is ready to
 * play". Called from HomePage's card onClick; ended either by
 * `endAudioReadyJourney` (on loadedmetadata or load error) or on a watchdog
 * if the user navigates away first.
 */
export function startAudioReadyJourney(
  sessionId: string,
  attributes: Attributes
): void {
  // If a previous click never resolved (user clicked a different card),
  // abort the dangling span so we don't leak.
  const stale = audioReadyJourneys.get(sessionId);
  if (stale) {
    stale.setAttribute('audio_ready.outcome', 'superseded');
    stale.end();
  }
  const span = tracer.startSpan('journey.audio_ready', { attributes });
  audioReadyJourneys.set(sessionId, span);
}

export function endAudioReadyJourney(
  sessionId: string,
  outcome: 'ready' | 'error' | 'aborted',
  extraAttributes: Attributes = {},
  error?: Error
): void {
  const span = audioReadyJourneys.get(sessionId);
  if (!span) return;
  span.setAttribute('audio_ready.outcome', outcome);
  for (const [k, v] of Object.entries(extraAttributes)) {
    if (v !== undefined) span.setAttribute(k, v);
  }
  if (outcome === 'error' && error) {
    span.recordException(error);
    span.setAttribute('error.type', error.name || 'Error');
  }
  span.end();
  audioReadyJourneys.delete(sessionId);
}

const LOGIN_JOURNEY_START_KEY = 'telemetry.login_journey_start';

/**
 * Record that the user clicked "sign in with Google". The OAuth flow
 * involves a full page redirect to Cognito + Google, so we can't keep an
 * in-memory span across it — we stash the start timestamp in sessionStorage
 * and `endLoginJourney` creates a single backdated span when the callback
 * page lands.
 */
export function startLoginJourney(): void {
  sessionStorage.setItem(LOGIN_JOURNEY_START_KEY, String(Date.now()));
}

export function endLoginJourney(
  outcome: 'success' | 'error',
  attributes: Attributes = {},
  error?: Error
): void {
  const startStr = sessionStorage.getItem(LOGIN_JOURNEY_START_KEY);
  if (!startStr) return;
  const startMs = parseInt(startStr, 10);
  if (!Number.isFinite(startMs)) {
    sessionStorage.removeItem(LOGIN_JOURNEY_START_KEY);
    return;
  }
  const span = tracer.startSpan('journey.login', {
    startTime: startMs,
    attributes: { 'login.method': 'google', ...attributes },
  });
  span.setAttribute('login.outcome', outcome);
  if (outcome === 'error' && error) {
    span.recordException(error);
    span.setAttribute('error.type', error.name || 'Error');
  }
  span.end();
  sessionStorage.removeItem(LOGIN_JOURNEY_START_KEY);
}

export { trace, context };
